import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  FileText,
  ArrowRight,
  Eye,
  EyeOff,
  LogOut,
  CreditCard,
  AlertCircle,
  Timer
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import bcrypt from 'bcryptjs';

interface ApplicationData {
  id: string;
  username: string;
  full_name: string;
  email: string;
  status: string;
  rejection_reason: string | null;
  status_acknowledged: boolean;
  position_number: number | null;
  created_at: string;
  approval_expires_at: string | null;
}

const AgentApplicationStatus = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [storedPasswordHash, setStoredPasswordHash] = useState<string | null>(null);

  const refreshPaidStatus = async (params: {
    applicationId: string;
    username: string;
    passwordHash: string;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('internal-agent-dashboard', {
        body: {
          applicationId: params.applicationId,
          username: params.username,
          passwordHash: params.passwordHash,
        },
      });

      if (error) return;
      if (!data?.success) return;

      const sub = data.subscription;
      const paid = sub?.status === 'active' && !!sub?.stripe_subscription_id;
      setHasPaidSubscription(paid);
    } catch {
      // silent: payment gating will default to requiring payment if we can't verify
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Get application by username (case-insensitive)
      const { data: apps, error } = await supabase
        .from('founding_agent_applications')
        .select('*')
        .ilike('username', credentials.username)
        .maybeSingle();

      if (error || !apps) {
        toast.error('Invalid username or password');
        setIsLoading(false);
        return;
      }

      // Verify password
      const isValid = await bcrypt.compare(credentials.password, apps.password_hash);
      
      if (!isValid) {
        toast.error('Invalid username or password');
        setIsLoading(false);
        return;
      }

      // Store the password hash for the setup flow
      setStoredPasswordHash(apps.password_hash);
      setApplication(apps as ApplicationData);
      setIsLoggedIn(true);

      // Set up session for header recognition
      const internalUser = {
        id: apps.id,
        username: apps.username,
        role: 'agent',
        age_group: 'adult',
        created_at: apps.created_at,
        updated_at: new Date().toISOString(),
        is_active: true,
        account_verified: true
      };

      localStorage.setItem('internal_user_session', JSON.stringify({
        user: internalUser,
        timestamp: Date.now()
      }));

      // Use localStorage instead of sessionStorage for persistence across refreshes
      localStorage.setItem('foundingAgentSession', JSON.stringify({
        applicationId: apps.id,
        username: apps.username,
        passwordHash: apps.password_hash,
        fullName: apps.full_name,
        email: apps.email,
        positionNumber: apps.position_number,
        isFoundingAgent: true,
        approvalExpiresAt: apps.approval_expires_at
      }));

      // Verify paid status via edge function (bypasses RLS)
      refreshPaidStatus({
        applicationId: apps.id,
        username: apps.username,
        passwordHash: apps.password_hash,
      });

      toast.success('Logged in successfully');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('foundingAgentSession');
    localStorage.removeItem('internal_user_session');
    setIsLoggedIn(false);
    setApplication(null);
    setCredentials({ username: '', password: '' });
    setStoredPasswordHash(null);
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const checkExistingSession = async () => {
      const savedSession = localStorage.getItem('foundingAgentSession');
      if (savedSession) {
        try {
          const session = JSON.parse(savedSession);
          // Verify the session is still valid by fetching the application
          const { data: apps, error } = await supabase
            .from('founding_agent_applications')
            .select('*')
            .eq('id', session.applicationId)
            .maybeSingle();

          if (!error && apps) {
            setApplication(apps as ApplicationData);
            setStoredPasswordHash(session.passwordHash);
            setIsLoggedIn(true);

            refreshPaidStatus({
              applicationId: session.applicationId,
              username: session.username,
              passwordHash: session.passwordHash,
            });
          } else {
            // Session invalid, clear it
            localStorage.removeItem('foundingAgentSession');
            localStorage.removeItem('internal_user_session');
          }
        } catch (e) {
          console.error('Error restoring session:', e);
          localStorage.removeItem('foundingAgentSession');
        }
      }
    };

    checkExistingSession();
  }, []);

  const handleProceedToPayment = async () => {
    if (!application || !storedPasswordHash) return;

    setIsLoading(true);
    try {
      // Call setup-founding-agent to acknowledge and set up session (no subscription creation)
      const { data, error } = await supabase.functions.invoke('setup-founding-agent', {
        body: {
          applicationId: application.id,
          username: application.username,
          passwordHash: storedPasswordHash
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Setup failed');

      setApplication({ ...application, status_acknowledged: true });
      
      // Update session with acknowledged status (using localStorage)
      localStorage.setItem('foundingAgentSession', JSON.stringify({
        applicationId: application.id,
        username: application.username,
        passwordHash: storedPasswordHash,
        fullName: application.full_name,
        email: application.email,
        positionNumber: application.position_number,
        isFoundingAgent: true,
        approvalExpiresAt: application.approval_expires_at
      }));
      
      toast.success('Proceeding to payment...');
      
      // Redirect to pricing page for payment
      navigate('/agent/pricing');
    } catch (error) {
      console.error('Error proceeding to payment:', error);
      toast.error('Failed to proceed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return { expired: true, text: 'Expired' };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return { expired: false, text: `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''} remaining` };
    }
    return { expired: false, text: `${hours} hour${hours > 1 ? 's' : ''} remaining` };
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Submitted',
          description: 'Your application has been received and is waiting for review.',
          icon: <FileText className="h-8 w-8" />,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/30'
        };
      case 'under_review':
        return {
          label: 'Under Review',
          description: 'Our team is currently reviewing your application. This typically takes 2-3 business days.',
          icon: <Clock className="h-8 w-8" />,
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500/30'
        };
      case 'approved':
        return {
          label: 'Approved',
          description: 'Congratulations! Your application has been approved. Complete your payment to secure your Founding Agent spot!',
          icon: <CheckCircle2 className="h-8 w-8" />,
          color: 'text-green-400',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/30'
        };
      case 'expired':
        return {
          label: 'Expired',
          description: 'Your approval has expired. The 7-day payment window has passed. You may reapply if spots are still available.',
          icon: <Timer className="h-8 w-8" />,
          color: 'text-orange-400',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/30'
        };
      case 'rejected':
        return {
          label: 'Not Approved',
          description: 'Unfortunately, your application was not approved at this time.',
          icon: <XCircle className="h-8 w-8" />,
          color: 'text-red-400',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30'
        };
      default:
        return {
          label: 'Unknown',
          description: 'Status unknown.',
          icon: <AlertCircle className="h-8 w-8" />,
          color: 'text-gray-400',
          bgColor: 'bg-gray-500/10',
          borderColor: 'border-gray-500/30'
        };
    }
  };

  // Check if user has already paid (has active subscription with stripe_subscription_id)
  const checkExistingSubscription = async () => {
    if (!application) return false;
    
    const { data } = await supabase
      .from('agent_subscriptions')
      .select('id, status, stripe_subscription_id')
      .eq('user_id', application.id)
      .eq('user_type', 'internal')
      .eq('status', 'active')
      .not('stripe_subscription_id', 'is', null)
      .maybeSingle();
    
    return !!data;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 pt-28 pb-16">
        <div className="max-w-xl mx-auto">
          {!isLoggedIn ? (
            // Login Form
            <Card className="border-2 border-primary/20 bg-card/80 backdrop-blur">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Check Application Status</CardTitle>
                <CardDescription>
                  Enter your credentials to view your Founding Agent application status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      value={credentials.username}
                      onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                      required
                      className="h-12"
                      placeholder="Enter your username"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={credentials.password}
                        onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                        required
                        className="h-12 pr-10"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Checking...' : 'View Status'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Don't have an application yet?{' '}
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-primary"
                      onClick={() => navigate('/agent-beta')}
                    >
                      Apply now
                    </Button>
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : application ? (
            // Status Display
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold">Application Status</h1>
                  <p className="text-muted-foreground">Welcome back, {application.full_name}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>

              {/* Status Card */}
              {(() => {
                const config = getStatusConfig(application.status);
                const timeRemaining = getTimeRemaining(application.approval_expires_at);
                
                return (
                  <Card className={`border-2 ${config.borderColor} ${config.bgColor}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className={`${config.color}`}>
                          {config.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={`${config.bgColor} ${config.color} border-0`}>
                              {config.label}
                            </Badge>
                            {application.position_number && (
                              <Badge variant="outline" className="border-primary/30 text-primary">
                                Founding Agent #{application.position_number}
                              </Badge>
                            )}
                          </div>
                          <p className="text-muted-foreground">{config.description}</p>

                          {/* Show expiration countdown for approved applications */}
                          {application.status === 'approved' && timeRemaining && !timeRemaining.expired && (
                            <Alert className="mt-4 border-yellow-500/50 bg-yellow-500/10">
                              <Timer className="h-4 w-4 text-yellow-600" />
                              <AlertDescription className="text-yellow-700">
                                <strong>Payment deadline:</strong> {timeRemaining.text}. 
                                Complete payment to secure your spot!
                              </AlertDescription>
                            </Alert>
                          )}

                          {/* Show rejection reason if rejected */}
                          {application.status === 'rejected' && application.rejection_reason && (
                            <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                              <p className="text-sm font-medium text-red-400">Reason:</p>
                              <p className="text-sm text-red-300">{application.rejection_reason}</p>
                            </div>
                          )}

                          {/* Payment / next step */}
                          {application.status === 'approved' && hasPaidSubscription ? (
                            <Button className="mt-4" onClick={() => navigate('/agent/dashboard')}>
                              Go to Dashboard
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          ) : (
                            <>
                              {/* Show Complete Payment button if approved and not acknowledged */}
                              {application.status === 'approved' && !application.status_acknowledged && (
                                <Button
                                  className="mt-4 bg-primary hover:bg-primary/90"
                                  onClick={handleProceedToPayment}
                                  disabled={isLoading}
                                >
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  {isLoading ? 'Processing...' : 'Complete Payment'}
                                  <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                              )}

                              {/* If acknowledged but not paid, show link to pricing */}
                              {application.status === 'approved' && application.status_acknowledged && (
                                <Button className="mt-4" onClick={() => navigate('/agent/pricing')}>
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  Complete Payment
                                  <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                              )}
                            </>
                          )}

                          {/* If expired, show reapply option */}
                          {application.status === 'expired' && (
                            <Button 
                              className="mt-4"
                              variant="outline"
                              onClick={() => navigate('/agent-beta')}
                            >
                              Reapply for Founding Agent
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Application Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Application Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-muted-foreground">Application ID</span>
                    <span className="font-mono text-sm">{application.id.slice(0, 8)}...</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-muted-foreground">Email</span>
                    <span>{application.email}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{new Date(application.created_at).toLocaleDateString()}</span>
                  </div>
                  {application.approval_expires_at && application.status === 'approved' && (
                    <div className="flex justify-between py-2 border-b border-white/10">
                      <span className="text-muted-foreground">Payment Deadline</span>
                      <span className="text-yellow-600 font-medium">
                        {new Date(application.approval_expires_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Help Section */}
              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground text-center">
                    Questions about your application?{' '}
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-primary"
                      onClick={() => navigate('/support')}
                    >
                      Contact Support
                    </Button>
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AgentApplicationStatus;